import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Fade,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Avatar
} from "@material-ui/core";
import Label from "components/Label";
import { Icon } from "@iconify/react";
import searchFill from "@iconify/icons-eva/search-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import { styled } from "@material-ui/core/styles";  
import { useTranslation } from "react-i18next";
import useWebotServer from "../hooks/useWebotServer";
import { ViewContactDialog } from "./_dashboard/contacts/table/rows/ViewContactDialog";
import {
  crmStatusLabels,
  getContactCrmIsHot,
  getContactCrmStatus,
  getContactCrmSubStatus,
  getContactCustomerTypes,
  SYSTEM_CONTACT_ORIGINS,
} from "../@types/sharedSchema";
import type {
  RealEstateContact,
  ProcessedListingAddress,
  REST_schema,
  FavoriteListingsOfContact
} from "../@types/sharedSchema";
import { getLocalPhoneNumberIfIsraeliNumber } from "utils/Haim/phoneNumbersUtils";
import { numberToCurrency } from "utils/listings/display";

const SearchContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  maxWidth: 900, // Increased from 400 to 900 (50% more)
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%"
  }
}));

const ownersToDisplayInSearch = 1;

const SearchDropdown = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 1300,
  maxHeight: 400,
  overflowY: "auto",
  marginTop: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[8]
}));

const SearchResult = styled(ListItem)(({ theme }) => ({
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));

interface ContactSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

function ContactSearch({ isOpen, onClose }: ContactSearchProps) {
  const { t } = useTranslation();
  const { contactSearch } = useWebotServer();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] =
    useState<REST_schema.ContactSearchResponseSchema | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const searchRequestIdRef = useRef<number>(0);

  // Contact dialog state
  const [selectedContact, setSelectedContact] = useState<RealEstateContact | null>(null);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [favoriteListingsOfContact, setFavoriteListingsOfContact] =
    useState<FavoriteListingsOfContact>({
      listingIds: [],
      realtorId: "",
      listingsListsIds: [],
      listingIdsUserLiked: [],
      listingIdsUserDisliked: [],
      listingIdsUserVisited: [],
      listingIdsWithPicturesHidden: [],
      additionalDetails: {}
    });

  // Clear search when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setSearchResults(null);
      setShowDropdown(false);
    }
  }, [isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node) &&
        showDropdown
      ) {
        setShowDropdown(false);
        setSearchTerm("");
        setSearchResults(null);
        onClose();
      }
    }

    if (isOpen && showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, showDropdown, onClose]);

  // Memoized search function to prevent unnecessary re-runs
  const performSearch = useCallback(
    async (term: string, requestId: number) => {
      try {
        const results = await contactSearch(term);

        // Only update results if this is still the latest search request
        if (requestId === searchRequestIdRef.current) {
          setSearchResults(results);
          setShowDropdown(true);
          setIsLoading(false);
        }
      } catch (error) {
        // Only handle error if this is still the latest search request
        if (requestId === searchRequestIdRef.current) {
          console.error("Search error:", error);
          setSearchResults({ results: [], totalResults: 0, searchTerm: term });
          setShowDropdown(true);
          setIsLoading(false);
        }
      }
    },
    [contactSearch]
  );

  // Debounced search effect
  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (searchTerm.length > 3) {
      // Increment request ID to track the latest search
      searchRequestIdRef.current += 1;
      const currentRequestId = searchRequestIdRef.current;

      setIsLoading(true);

      debounceTimeoutRef.current = setTimeout(() => {
        performSearch(searchTerm, currentRequestId);
      }, 300);
    } else {
      // Reset search state for short terms
      setShowDropdown(false);
      setSearchResults(null);
      setIsLoading(false);
      searchRequestIdRef.current += 1; // Cancel any ongoing searches
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  function handleClose() {
    setSearchTerm("");
    setSearchResults(null);
    setShowDropdown(false);
    onClose();
  }

  function handleContactClick(result: REST_schema.ContactSearchResult) {
    // Close the dropdown first
    setShowDropdown(false);

    // Convert the search result to RealEstateContact format
    const firstContactObject = result.contactObjects[0];
    if (firstContactObject) {
      // Cast to RealEstateContact to access the properties safely
      const contactAsRealEstate = firstContactObject as RealEstateContact & {
        user: REST_schema.MiniUser;
      };

      // Ensure the identity is set: the document id IS the contact's phone.
      const realEstateContact: RealEstateContact = {
        ...firstContactObject,
        id: firstContactObject.id ?? result.phoneNumberInternational,
        // Ensure all required fields are present with proper fallbacks
        customerType: contactAsRealEstate.customerType || null,
        customerBudget: contactAsRealEstate.customerBudget || 0,
        street: contactAsRealEstate.street || "",
        neighborhood: contactAsRealEstate.neighborhood || "",
        roomsMin: contactAsRealEstate.roomsMin || null,
        roomsMax: contactAsRealEstate.roomsMax || null,
        floorsMin: contactAsRealEstate.floorsMin || null,
        floorsMax: contactAsRealEstate.floorsMax || null,
        // CRM v2 axes, not the retired `customerSeriousness` mirror — so the picked
        // contact renders the same status chip everywhere else does.
        crmStatus: getContactCrmStatus(contactAsRealEstate),
        crmSubStatus: getContactCrmSubStatus(contactAsRealEstate),
        crmIsHot: getContactCrmIsHot(contactAsRealEstate),
        contactOrigin: firstContactObject.contactOrigin || SYSTEM_CONTACT_ORIGINS.dashboardSearch
      };

      setSelectedContact(realEstateContact);
      setFavoriteListingsOfContact({
        listingIds: [],
        realtorId: firstContactObject.user?.userMail || "",
        listingsListsIds: [],
        listingIdsUserLiked: [],
        listingIdsUserDisliked: [],
        listingIdsUserVisited: [],
        listingIdsWithPicturesHidden: [],
        additionalDetails: {}
      });
      setIsContactDialogOpen(true);
    }
  }

  function handleContactDialogClose() {
    setIsContactDialogOpen(false);
    setSelectedContact(null);
  }

  function handleEditContactDialogOpen() {
    // This function is required by ViewContactDialog but we don't need it for search
    console.log("Edit contact dialog open - not implemented in search context");
  }

  function formatPhoneNumber(phone: string) {
    // Simple phone number formatting
    return phone.replace(/(\+\d{1,3})(\d{2,3})(\d{3,4})(\d{4})/, "$1 $2-$3-$4");
  }

  function formatLocations(locations: ProcessedListingAddress[]): string {
    if (!locations || locations.length === 0) return "-";
    return locations
      .map((location) => {
        const parts = [location.neighborhood, location.city].filter(Boolean);
        return parts.join(", ");
      })
      .join(" • ");
  }

  function renderRealEstateContactDetails(contact: RealEstateContact) {
    const labels = [];

    if (contact.customerBudget) {
      labels.push(
        <Label key="budget" color="primary">
          {numberToCurrency(contact.customerBudget)}
        </Label>
      );
    }

    if (contact.interestedInLocations && contact.interestedInLocations.length > 0) {
      labels.push(
        <Label key="locations" color="info">
          {formatLocations(contact.interestedInLocations)}
        </Label>
      );
    }

    const contactCustomerTypes = getContactCustomerTypes(contact);
    if (contactCustomerTypes.length > 0) {
      labels.push(
        <Label key="type" color="secondary">
          {contactCustomerTypes.join(" · ")}
        </Label>
      );
    }

    // CRM v2 lifecycle status (+ the independent 🔥 flag). The retired
    // `customerSeriousness` mirror is never rendered — see docs/crm/crm-status-model.md.
    const crmStatus = getContactCrmStatus(contact);
    const isHotContact = getContactCrmIsHot(contact);
    if (crmStatus || isHotContact) {
      labels.push(
        <Label key="crmStatus" color={isHotContact ? "warning" : "default"}>
          {`${crmStatus ? crmStatusLabels[crmStatus] : ""}${isHotContact ? " 🔥" : ""}`.trim()}
        </Label>
      );
    }

    if (contact.roomsMin || contact.roomsMax) {
      const roomsText =
        contact.roomsMin && contact.roomsMax
          ? `${contact.roomsMin} - ${contact.roomsMax} ${t("search.contacts.roomsLabel", {
              defaultValue: "rooms"
            })}`
          : `${contact.roomsMin || contact.roomsMax} ${t("search.contacts.roomsLabel", {
              defaultValue: "rooms"
            })}`;

      labels.push(
        <Label key="rooms" color="default">
          {roomsText}
        </Label>
      );
    }

    return (
      <Box sx={{ p: 2, pt: 1 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          {labels}
        </Stack>
      </Box>
    );
  }

  if (!isOpen) return null;

  return (
    <Fade in={isOpen}>
      <SearchContainer ref={searchContainerRef}>
        <TextField
          ref={searchInputRef}
          fullWidth
          variant="outlined"
          size="small"
          focused={true}
          autoFocus={true}
          placeholder={t("search.contacts.placeholder") || "Search contacts..."}
          value={searchTerm}
          onChange={handleInputChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon={searchFill} width={20} height={20} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton size="small" onClick={handleClose}>
                    <Icon icon={closeFill} width={20} height={20} />
                  </IconButton>
                )}
              </InputAdornment>
            )
          }}
          sx={{
            backgroundColor: "background.paper",
            "& .MuiOutlinedInput-root": {
              "&:hover fieldset": {
                borderColor: "primary.main"
              }
            }
          }}
        />

        {showDropdown && searchResults && (
          <SearchDropdown>
            {searchResults.results?.length > 0 ? (
              <>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("search.contacts.found", {
                      count: searchResults.totalResults,
                      defaultValue: `Found ${searchResults.totalResults} contacts`
                    })}
                  </Typography>
                </Box>
                <List>
                  {searchResults.results.map((result, index) => {
                    const contactId = `${result.phoneNumberInternational}-${index}`;
                    const isLastItem = index === searchResults.results.length - 1;
                    const realEstateContact = result.contactObjects.find(
                      (contact) =>
                        "customerBudget" in contact ||
                        "interestedInLocations" in contact ||
                        "customerType" in contact
                    ) as (RealEstateContact & { user: REST_schema.MiniUser }) | undefined;

                    return (
                      <Box key={contactId}>
                        <SearchResult
                          onClick={() => handleContactClick(result)}
                          sx={{
                            display: "block",
                            "& .MuiListItemText-root": {
                              margin: 0
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between"
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography variant="subtitle2">{result.displayName}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {getLocalPhoneNumberIfIsraeliNumber(
                                      result.phoneNumberInternational
                                    )}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Box
                                    sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
                                  >
                                    {result.contactObjects
                                      .slice(0, ownersToDisplayInSearch)
                                      .map((contactObj, idx) => (
                                        <Box
                                          key={idx}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            mr:
                                              idx <
                                              Math.min(
                                                result.contactObjects.length,
                                                ownersToDisplayInSearch
                                              ) -
                                                1
                                                ? 1
                                                : 0
                                          }}
                                        >
                                          <Avatar
                                            src={contactObj.user?.userAvatar}
                                            alt={contactObj.user?.userName}
                                            sx={{ width: 20, height: 20 }}
                                          />
                                          <Typography variant="caption" color="text.secondary">
                                            {contactObj.user?.userName ||
                                              contactObj.user?.userMail ||
                                              contactObj.userId}
                                          </Typography>
                                        </Box>
                                      ))}
                                    {result.contactObjects.length > ownersToDisplayInSearch && (
                                      <Chip
                                        size="small"
                                        label={`+${
                                          result.contactObjects.length - ownersToDisplayInSearch
                                        }`}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: "0.65rem" }}
                                      />
                                    )}
                                  </Box>
                                  {result.contactObjects.length > 0 &&
                                    result.contactObjects[0].additionalNotes && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          mt: 0.5,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: 300
                                        }}
                                      >
                                        {result.contactObjects[0].additionalNotes}
                                      </Typography>
                                    )}
                                </Box>
                              }
                            />
                          </Box>
                        </SearchResult>
                        {!isLastItem && <Divider />}
                      </Box>
                    );
                  })}
                </List>
              </>
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("search.contacts.noResults", {
                    defaultValue: "No contacts found"
                  })}
                </Typography>
              </Box>
            )}
          </SearchDropdown>
        )}

        {searchTerm.length > 0 && searchTerm.length <= 1 && (
          <SearchDropdown>
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("search.contacts.minChars", {
                  defaultValue: "Type at least 2 characters to search"
                })}
              </Typography>
            </Box>
          </SearchDropdown>
        )}

        {/* Contact Dialog */}
        {selectedContact && (
          <ViewContactDialog
            open={isContactDialogOpen}
            contact={selectedContact}
            favoriteListingsOfContact={favoriteListingsOfContact}
            contactTableAdditionalProps={{
              currentTab: null,
              handleClose: handleContactDialogClose,
              initialNestedTab: "timeline"
            }}
            handleEditContactDialogOpen={handleEditContactDialogOpen}
          />
        )}
      </SearchContainer>
    </Fade>
  );
}

export default ContactSearch;
